/* eslint-disable react-refresh/only-export-components -- shared primitives
   module: intentionally exports components alongside a few helper constants /
   hooks (PROVIDER_BLURB, useProviderLogos), same pattern as ui/Icon.tsx. */
// Shared presentational building blocks for the Agent / Providers / Models
// settings panels. Assembled from the design-system row/card/tag primitives
// (same patterns as SourcesPanel / AccountsPanel) — no invented styling.
import { useEffect, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";
import type {
  AiModel,
  AiProvider,
  CatalogModel,
  CatalogResponse,
  SubscriptionStatus,
} from "./types";
import { Icon, Tag } from "../../components/ui";

// API-key fields are NOT login passwords: presenting them as `type=password`
// makes Chrome/1Password/LastPass pop their "fill generated password" overlay
// over the row. We render a plain text field (managers ignore it), mask the
// characters via CSS (`-webkit-text-security`), and turn every autofill hint
// off. Spread on each secret input.
export const SECRET_INPUT_PROPS = {
  type: "text" as const,
  autoComplete: "off" as const,
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
  name: "magnis-provider-api-key",
  "data-1p-ignore": "true",
  "data-lpignore": "true",
  "data-form-type": "other",
} as const;

// Short blurbs for the "Available" providers list (content, not layout).
export const PROVIDER_BLURB: Readonly<Record<string, string>> = {
  anthropic: "Claude models — Sonnet, Opus, Haiku",
  openai: "GPT and o-series models",
  openrouter: "100+ models through a single key",
  "codex-subscription": "Use your OpenAI subscription",
};

// ── Catalog price badge ("$3 / $15" per 1M tokens) ──

export function catalogPrice(m: CatalogModel): string | null {
  const inC = m.cost_input_usd_mtok;
  const outC = m.cost_output_usd_mtok;
  if (inC == null && outC == null) return null;
  return `$${inC ?? "—"} / $${outC ?? "—"}`;
}

// ── Section label + grouped-card shells (copied from SourcesPanel /
//    AccountsPanel: one card, divided rows — never a card per item) ──

export function GroupLabel({ children }: { readonly children: string }): JSX.Element {
  return <span className="text-xs text-content-tertiary">{children}</span>;
}

export function GroupCard({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
      {children}
    </div>
  );
}

// ── Modal shell (same pattern as SetValueModal / AddAccountModal: backdrop +
//    centered card, close on backdrop click). ──

export function Modal({
  title,
  subtitle,
  children,
  onClose,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md mx-4 rounded-xl bg-surface border border-edge shadow-2xl p-5">
        <h2 className="text-[15px] font-semibold text-content m-0">{title}</h2>
        {subtitle !== undefined && (
          <p className="text-xs text-content-secondary mt-1 mb-3">{subtitle}</p>
        )}
        {children}
      </div>
    </div>
  );
}

const SECRET_FIELD_CLASS =
  "rounded-lg border border-edge bg-surface-tertiary px-3 py-2 text-sm text-content outline-none focus:border-content-muted [-webkit-text-security:disc]";

function ModalActions({
  busy,
  disabled,
  submitLabel,
  onClose,
}: {
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly submitLabel: string;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onClose}
        className="text-xs px-3 py-1.5 rounded border border-edge text-content-secondary hover:text-content"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={busy || disabled}
        className="text-xs px-3 py-1.5 rounded bg-accent text-on-bright disabled:opacity-50 border-none cursor-pointer"
      >
        {busy ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

// ── Provider key modal (Connect / Replace key). For a subscription provider it
//    shows the host-login hint instead of a key field. ──

export function ProviderKeyModal({
  provider,
  onSave,
  onEnable,
  onClose,
}: {
  readonly provider: AiProvider;
  readonly onSave: (key: string) => Promise<void>;
  /** Subscription providers have no key — "Use" just enables them. */
  readonly onEnable?: () => Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (provider.auth_kind === "codex_subscription") {
    return (
      <Modal
        title={`Use ${provider.name}`}
        subtitle="Runs the Magnis Agent on your OpenAI subscription — no API key, usage billed at $0."
        onClose={onClose}
      >
        <p className="text-sm text-content-secondary">
          Make sure you have run <span className="text-content font-mono">codex login</span> on the
          agent host. Enabling adds a default Codex model and points the Magnis Agent at it.
        </p>
        {error !== null && <p className="text-xs text-red-400 mt-2 mb-0">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-edge text-content-secondary hover:text-content"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || onEnable === undefined}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async () => {
              if (!onEnable) return;
              setBusy(true);
              setError(null);
              try {
                await onEnable();
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                setBusy(false);
              }
            }}
            className="text-xs px-3 py-1.5 rounded bg-accent text-on-bright disabled:opacity-50 border-none cursor-pointer"
          >
            {busy ? "Enabling…" : "Enable"}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`${provider.api_key_set ? "Replace key" : "Connect"} · ${provider.name}`}
      subtitle="Your API key is stored encrypted and never shown again."
      onClose={onClose}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (busy || value.trim().length === 0) return;
          setBusy(true);
          setError(null);
          void onSave(value.trim())
            .then(onClose)
            .catch((err: unknown) => {
              setError(err instanceof Error ? err.message : String(err));
              setBusy(false);
            });
        }}
      >
        <input
          {...SECRET_INPUT_PROPS}
          autoFocus
          value={value}
          onChange={(e) => { setValue(e.target.value); }}
          placeholder="Paste API key"
          className={SECRET_FIELD_CLASS}
        />
        {error !== null && <p className="text-xs text-red-400 m-0">{error}</p>}
        <ModalActions
          busy={busy}
          disabled={value.trim().length === 0}
          submitLabel="Save"
          onClose={onClose}
        />
      </form>
    </Modal>
  );
}

// ── Provider kind badge + subscription status (compact, no raw error dump) ──

export function providerKindTag(provider: AiProvider): JSX.Element {
  if (provider.auth_kind === "codex_subscription") {
    return <Tag label="Subscription" variant="purple" mode="subtle" />;
  }
  return <Tag label={provider.reserved ? "API key" : "Custom"} mode="subtle" />;
}

export const SUB_TONE = {
  green: { dot: "bg-green-400", text: "text-green-400" },
  red: { dot: "bg-red-400", text: "text-red-400" },
  grey: { dot: "bg-content-tertiary", text: "text-content-tertiary" },
} as const;

export function SubStatus({
  status,
  statusError,
}: {
  readonly status: SubscriptionStatus | null;
  readonly statusError: string | null;
}): JSX.Element {
  const connected = status?.connected === true;
  const unreachable = status === null && statusError !== null;
  const tone = connected ? "green" : unreachable ? "red" : "grey";
  const label = connected ? "Connected" : unreachable ? "Unreachable" : "Checking…";
  return (
    <span
      className="flex items-center gap-1.5"
      title={unreachable ? statusError : undefined}
    >
      <span className={`w-2 h-2 rounded-full ${SUB_TONE[tone].dot}`} />
      <span className={`text-xs ${SUB_TONE[tone].text}`}>{label}</span>
    </span>
  );
}

// ── Connected provider row (inside the "Connected" GroupCard) ──

export function ConnectedRow({
  provider,
  status,
  statusError,
  onReplaceKey,
  onDisconnect,
}: {
  readonly provider: AiProvider;
  readonly status: SubscriptionStatus | null;
  readonly statusError: string | null;
  readonly onReplaceKey: () => void;
  readonly onDisconnect: () => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSub = provider.auth_kind === "codex_subscription";

  const disconnect = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onDisconnect();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm font-medium text-content truncate">{provider.name}</span>
          {providerKindTag(provider)}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isSub ? (
            <>
              <SubStatus status={status} statusError={statusError} />
              <button
                type="button"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={disconnect}
                disabled={busy}
                title="Stop using this subscription"
                className="text-xs px-2.5 py-1 rounded-md border border-edge text-content-muted hover:text-red-400 hover:border-red-500/40 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              >
                Disconnect
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onReplaceKey}
                className="text-xs px-2.5 py-1 rounded-md border border-edge text-content-secondary hover:text-content hover:border-edge-strong transition-colors"
              >
                Replace key
              </button>
              {!provider.reserved && (
                <button
                  type="button"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={disconnect}
                  disabled={busy}
                  className="text-xs px-2.5 py-1 rounded-md border border-edge text-content-muted hover:text-red-400 hover:border-red-500/40 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                >
                  Disconnect
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {error !== null && <p className="px-4 pb-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── Available provider row (inside the "Available" GroupCard) ──

export function AvailableRow({
  provider,
  blurb,
  onConnect,
}: {
  readonly provider: AiProvider;
  readonly blurb: string | undefined;
  readonly onConnect: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <span className="text-sm font-medium text-content">{provider.name}</span>
        {blurb !== undefined && (
          <p className="text-xs text-content-tertiary truncate">{blurb}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onConnect}
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-edge text-content-secondary hover:text-content hover:border-edge-strong transition-colors shrink-0"
      >
        <Icon name="plus" size={12} />
        Connect
      </button>
    </div>
  );
}

// ── Model picker (flat, searchable models.dev catalog) ──

export function ModelPicker({
  enabledModels,
  connectedProviderIds,
  catalogModels,
  onEnable,
}: {
  readonly enabledModels: readonly AiModel[];
  readonly connectedProviderIds: ReadonlySet<string>;
  readonly catalogModels: (search: string) => Promise<CatalogResponse>;
  readonly onEnable: (row: CatalogModel) => Promise<void>;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<readonly CatalogModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      void catalogModels(q)
        .then((res) => {
          if (!cancelled) {
            setResults(res.models);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            // Clear stale rows so the error doesn't sit above results from a
            // previous query the user can still act on (review F4).
            setResults([]);
            setError(err instanceof Error ? err.message : String(err));
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return (): void => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, catalogModels]);

  const enabledKeys = useMemo(
    () => new Set(enabledModels.map((m) => `${m.provider_id}/${m.model_id}`)),
    [enabledModels],
  );

  // Каталог отдаёт модели всех 100+ провайдеров models.dev, но включить
  // (FK на ai_providers) можно только у ПОДКЛЮЧЁННЫХ. Показываем в пикере
  // лишь их — иначе Add упирался бы в «provider not connected».
  const visible = useMemo(
    () => results.filter((r) => connectedProviderIds.has(r.provider_id)),
    [results, connectedProviderIds],
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleEnable = async (row: CatalogModel) => {
    const key = `${row.provider_id}/${row.model_id}`;
    setAdding(key);
    setError(null);
    try {
      await onEnable(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary">
          <Icon name="search" size={14} />
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          placeholder="Add or search model..."
          className="text-sm bg-surface border border-edge rounded-lg pl-8 pr-3 py-2 text-content w-full focus:outline-none focus:border-accent"
        />
      </div>

      {error !== null && (
        <span className="text-xs text-red-400 block">{error}</span>
      )}

      {query.trim() && (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-edge">
          {loading && (
            <div className="py-3 text-center text-content-tertiary text-xs">Searching...</div>
          )}
          {!loading && visible.length === 0 && results.length > 0 && (
            <div className="py-3 text-center text-content-muted text-xs">
              Matching models belong to providers you haven&apos;t connected — add the provider first.
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="py-3 text-center text-content-muted text-xs">No models match.</div>
          )}
          {!loading &&
            visible.map((row) => {
              const key = `${row.provider_id}/${row.model_id}`;
              const already = enabledKeys.has(key);
              const price = catalogPrice(row);
              return (
                <div
                  key={key}
                  className="flex items-center gap-3 py-2 px-3 border-b border-edge last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-content truncate">{row.name}</span>
                      <span className="text-xs text-content-tertiary shrink-0">{row.provider_id}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {price !== null && (
                        <span className="text-xs text-content-tertiary">{price}</span>
                      )}
                      {row.context_limit != null && (
                        <span className="text-[10px] text-content-muted">
                          {Math.round(row.context_limit / 1000)}K ctx
                        </span>
                      )}
                    </div>
                  </div>
                  {already ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">
                      added
                    </span>
                  ) : (
                    <button
                      type="button"
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onClick={() => handleEnable(row)}
                      disabled={adding === key}
                      className="text-xs px-3 py-1.5 rounded bg-accent text-white font-medium disabled:opacity-50"
                    >
                      Add
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

// ── Enabled model row ──

export function ModelRow({
  model,
  isDefault,
  onSetDefault,
  onDelete,
}: {
  readonly model: AiModel;
  readonly isDefault: boolean;
  readonly onSetDefault: () => Promise<void>;
  readonly onDelete: () => Promise<void>;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const runAction = async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm text-content truncate">{model.name}</span>
          <span className="text-xs text-content-tertiary shrink-0">{model.provider_id}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isDefault ? (
            <Tag label="Default" variant="orange" mode="subtle" />
          ) : (
            <button
              type="button"
              onClick={() => { void runAction(onSetDefault); }}
              className="text-xs px-2.5 py-1 rounded-md border border-edge text-content-secondary hover:text-content hover:border-edge-strong transition-colors"
            >
              Set default
            </button>
          )}
          {!isDefault && (
            <button
              type="button"
              onClick={() => { void runAction(onDelete); }}
              title="Remove model"
              aria-label={`Remove ${model.name}`}
              className="p-1 rounded-md text-content-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Icon name="trash" size={14} />
            </button>
          )}
        </div>
      </div>
      {error !== null && (
        <span className="text-xs text-red-400 block px-4 pb-2">{error}</span>
      )}
    </div>
  );
}

// ── Agent (engine) metadata. Claude Code / Codex are transitional — the plan
//    is for Magnis Agent to become the only agent. ──

export const ENGINE_META: Readonly<Record<string, { label: string; subtitle: string }>> = {
  builtin: { label: "Magnis Agent", subtitle: "Any model you connect" },
  claude: { label: "Claude Code", subtitle: "Uses your Claude subscription" },
  codex: { label: "Codex", subtitle: "OpenAI subscription" },
};

export function engineMeta(name: string): { label: string; subtitle: string } {
  return ENGINE_META[name] ?? { label: name, subtitle: "" };
}

// ── Agent row: pick the default agent; right side shows a per-agent status. ──

export function AgentRow({
  name,
  active,
  status,
  onSelect,
}: {
  readonly name: string;
  readonly active: boolean;
  readonly status: ReactNode;
  readonly onSelect: () => void;
}): JSX.Element {
  const meta = engineMeta(name);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex items-center justify-between gap-3 w-full px-4 py-3 text-left hover:bg-surface-hover transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-3 min-w-0">
        {active ? (
          <span className="w-5 h-5 rounded-full border-2 border-accent flex items-center justify-center shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-accent" />
          </span>
        ) : (
          <span className="w-5 h-5 rounded-full border-2 border-content-muted shrink-0" />
        )}
        <div className="min-w-0">
          <span className={`text-sm font-medium ${active ? "text-content" : "text-content-secondary"}`}>
            {meta.label}
          </span>
          <p className="text-xs text-content-tertiary">{meta.subtitle}</p>
        </div>
      </div>
      <div className="shrink-0">{status}</div>
    </button>
  );
}

// ── Main Panel ──

